import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { axiosInstance } from '../axios-instance';
import { DateTimeInterval, Location, LocationSearchPaginationRequest } from './location/location';
import { DateTime } from 'luxon';
import {
  AssignableUnit,
  ConfirmAvailability,
  ProductCalendar,
  ProductQuantityAvailability,
  ProductSchedule,
  ProductUtilization,
} from './availability/availability';
import {
  AssignDriverRequest,
  CreateOrderRequest,
  CreateQuoteRequest,
  Dispatch,
  Order,
  OrderPaymentMethod,
  OrderProduct,
  OrderType,
  SearchDispatchRequest,
  UpdateScheduledTimeRequest,
} from './order/order';
import { useContext } from 'react';
import { AvailabilityParsers } from './availability/availability-parsers';
import { LocationParsers } from './location/location-parsers';
import { OrderParsers } from './order/order-parsers';
import {
  EquipmentListingResponse,
  ListOnBoomAndBucketRequest,
  Make,
  Model,
  Product,
  ProductInventoryUnit,
  ProductUnitValuation,
  ProductUpdateRequest,
  SearchProductInventoryUnitsRequest,
} from './product/product';
import { ProductParsers } from './product/product-parsers';
import { TenantContext } from '../../context/tenant-context';
import {
  CalculatePriceRequest,
  CalculatePriceResult,
  CreateProductPriceRequest,
  DeadDaysConfig,
  EditProductPriceRequest,
  ProductPrice,
} from './pricing/pricing';
import { PricingParsers } from './pricing/pricing-parsers';
import { Configuration, Country } from './configuration/configuration';
import { ConfigurationParsers } from './configuration/configuration-parsers';
import { Pagination } from './pagination/pagination';
import { useProductApiV1 } from './product/product-api-v1';
import { HourReadingParsers } from './hour-reading/hour-reading-parsers';
import { ContextParsers } from './context/context-parsers';
import { AdminContext } from './context/context';
import { PaginationParsers } from './pagination/pagination-parsers';
import { useReportApiV1 } from './report/report-api-v1';
import { useServiceAndRepairAPI } from './service-and-repair/service-and-repair-api';
import { TagPricing, TagPricingSearchRequest } from './user/user';
import { Label } from './label/label';
import { AccountRequest, AccountSearchRequest } from './payment/on-account/on-account';
import { OnAccountParsers } from './payment/on-account/on-account-parsers';
import { CalculateFinancialSummaryRequest, FinancialSummary } from './financials/financials';
import { FinancialsParsers } from './financials/financials-parsers';
import { CouponParsers } from './coupon/coupon-parsers';
import { OrderReview, OrderReviewSearchRequest, OrderReviewStatistics } from './customer-reviews/order-reviews';
import { OrderReviewParsers } from './customer-reviews/order-reviews-parsers';
import { EmailArchive, EmailMessage, SearchEmailArchivesRequest, EmailTemplate, EmailDefault } from './email/email';
import { EmailParsers } from './email/email-parsers';
import { CancelReason } from './cancel-reason/cancel-reasons';
import { PaymentMethodRequest } from './payment/payment';
import { EntityType, Event, EventType } from './events/events';
import { EventParsers } from './events/events-parser';
import { NotificationRequest } from './notfication/notification';
import GetProductSchedulesResponseJson = AvailabilityParsers.ProductSchedulesJson;
import OrderProductJson = OrderParsers.OrderProductJson;
import OrderJson = OrderParsers.OrderJson;
import { useDeliveryApi } from './delivery/delivery-api';
import { useUserAPI } from './user/user-api';
import { useOrderApiV1 } from './order/order-api-v1';

export const useAPI = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function getAdminContext(tenant: string): Promise<AdminContext> {
    const config = {
      method: 'get',
      url: `${tenant}/quipli-service/yorent/v1/admins/context`,
    };

    const result = await handleRequest<ContextParsers.ContextJson>(config);
    return result && ContextParsers.toContext(result);
  }

  async function getConfiguration(configurationName: string): Promise<Configuration | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/configurations/${configurationName}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<ConfigurationParsers.ConfigurationJson | undefined>(config);
    return result && ConfigurationParsers.toConfiguration(result);
  }

  async function updateConfiguration(name: string, value: string): Promise<void> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/configurations/${name}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { value },
    };
    await handleRequest<ConfigurationParsers.ConfigurationJson[]>(config);
  }

  async function getMultipleConfigurations(configurationNames: string[]): Promise<Configuration[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/configurations/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: configurationNames,
    };
    const result = await handleRequest<ConfigurationParsers.ConfigurationJson[]>(config);
    return result.map(ConfigurationParsers.toConfiguration);
  }

  async function getEmailTemplates(): Promise<EmailTemplate[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/emails/template`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest(config);
  }

  async function searchEmailTemplates(userType?: 'Customer' | 'Admin'): Promise<EmailTemplate[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/emails/template/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        userType,
      },
    };
    return await handleRequest(config);
  }

  async function getTemplateDefault(template: EmailTemplate): Promise<EmailDefault> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/emails/template/${template.name}/default`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest(config);
  }

  async function getReplacementVariables(template: EmailTemplate): Promise<Record<string, string>> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/emails/template/${template.id}/replacement-variables`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest(config);
  }

  async function updateEmailTemplate(template: EmailTemplate): Promise<EmailTemplate> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/emails/template/${template.id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: template,
    };
    return await handleRequest(config);
  }

  async function getCountry(): Promise<Country> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/configurations/country`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<ConfigurationParsers.CountryJson>(config);
    return ConfigurationParsers.toCountry(result);
  }

  async function getQuantityAvailability(
    productIds: number[] | undefined,
    locationIds: number[] | undefined,
    from?: DateTime,
    to?: DateTime,
    includeBuffers?: boolean,
  ): Promise<ProductQuantityAvailability[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/availability/getQuantityAvailability`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productIds,
        locationIds,
        from,
        to,
        includeBuffers,
      },
    };
    const result = await handleRequest<AvailabilityParsers.ProductQuantityAvailabilityJson[]>(config);
    return result.map(AvailabilityParsers.toProductQuantityAvailability);
  }

  async function getProductCalendar(
    productIds: number[] | undefined,
    locationIds: number[] | undefined,
    from?: DateTime,
    to?: DateTime,
    includeBuffers?: boolean,
    includeSoldUnits?: boolean,
  ): Promise<ProductCalendar[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/availability/getProductCalendar`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productIds,
        locationIds,
        from,
        to,
        includeBuffers,
        includeSoldUnits,
      },
    };
    const result = await handleRequest<AvailabilityParsers.ProductCalendarJson[]>(config);
    return result.map(AvailabilityParsers.toProductCalendar);
  }

  async function getProductSchedules(
    productIds: number[] | undefined,
    locationIds: number[] | undefined,
    from: DateTime,
    to: DateTime,
    includeSoldUnits?: boolean,
    keepSoldBeforeFromDate?: boolean,
  ): Promise<ProductSchedule[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/availability/getProductSchedules`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productIds,
        locationIds,
        from: from.toISO(),
        to: to.toISO(),
        includeSoldUnits,
        keepSoldBeforeFromDate,
      },
    };
    const result = await handleRequest<GetProductSchedulesResponseJson[]>(config);
    return result.map(AvailabilityParsers.toProductSchedule);
  }

  async function getLocationById(locationId: number): Promise<Location> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/locations/${locationId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<LocationParsers.LocationJson>(config);
    return LocationParsers.toLocation(result);
  }

  async function listLocations(isEnabled?: boolean): Promise<Location[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/locations`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        isEnabled,
      },
    };
    const result = await handleRequest<LocationParsers.LocationJson[]>(config);
    return result.map(LocationParsers.toLocation);
  }

  async function calculateUtilization(
    productIds: number[] | undefined,
    locationIds: number[] | undefined,
    categoryIds: number[] | undefined,
    from: DateTime,
    to: DateTime,
  ): Promise<ProductUtilization[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/availability/calculateUtilization`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productIds,
        locationIds,
        categoryIds,
        from,
        to,
      },
    };
    const result = await handleRequest<AvailabilityParsers.ProductUtilizationJson[]>(config);
    return result.map(AvailabilityParsers.toProductUtilization);
  }

  async function getOrderProductById(orderId: number, orderProductId: number): Promise<OrderProduct> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/products/${orderProductId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const op = await handleRequest<OrderParsers.OrderProductJson>(config);
    return OrderParsers.toOrderProduct(op);
  }

  async function assignUnits(orderId: number, orderProductId: number, inventoryUnits: number[]): Promise<OrderProduct> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/products/${orderProductId}/assignUnits`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { inventoryUnits },
    };

    const op = await handleRequest<OrderProductJson>(config);
    return OrderParsers.toOrderProduct(op);
  }

  async function swapUnits(
    orderId: number,
    orderProductId: number,
    currentUnitId: number,
    replacementUnitId: number,
    createOutage: boolean,
    hourReading?: number,
  ): Promise<OrderProduct> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/products/${orderProductId}/swapUnits`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { currentUnitId, replacementUnitId, createOutage, hourReading },
    };

    const op = await handleRequest<OrderProductJson>(config);
    return OrderParsers.toOrderProduct(op);
  }

  async function listProducts(): Promise<Product[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<ProductParsers.ProductJson[]>(config);
    return result.map(ProductParsers.toProduct);
  }

  async function getMultipleProductsByIds(ids: number[]): Promise<Product[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: ids,
    };

    const result = await handleRequest<ProductParsers.ProductJson[]>(config);
    return result.map(ProductParsers.toProduct);
  }

  async function createPrice(body: CreateProductPriceRequest): Promise<ProductPrice> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/product/prices`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleRequest<ProductPrice>(config);
  }

  async function editPrice(priceId: string, body: EditProductPriceRequest): Promise<ProductPrice> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/product/prices/${priceId}`,
      headers: { 'X-Tenant': tenant },
      data: body,
    };
    return await handleRequest<ProductPrice>(config);
  }

  async function deletePrice(priceId: string): Promise<ProductPrice> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/product/prices/${priceId}`,
      headers: { 'X-Tenant': tenant },
    };
    return await handleRequest<ProductPrice>(config);
  }

  async function listProductPrices(productId: number): Promise<ProductPrice[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/product/prices?productId=${productId}&sortBy=price&includeDeleted=false&includeInactive=false&durationDisplayLabel=true`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<PricingParsers.ProductPriceJson[]>(config);
    return result.map((r) => PricingParsers.toProductPrice(r));
  }

  async function listProductPricesForOrder(orderId: number, locationId: number): Promise<ProductPrice[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/product/prices/by-order/${orderId}?locationId=${locationId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<PricingParsers.ProductPriceJson[]>(config);
    return result.map((r) => PricingParsers.toProductPrice(r));
  }

  async function getAssignableUnits(
    productId: number,
    locationId: number,
    from: DateTime,
    to?: DateTime,
    includeBuffers?: boolean,
    excludedOrderIds?: number[],
    type?: OrderType,
  ): Promise<AssignableUnit[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/availability/getAvailableUnits`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productId,
        locationId,
        from,
        to,
        includeBuffers,
        excludedOrderIds,
        type,
      },
    };
    const result = await handleRequest<AvailabilityParsers.AssignableUnitJson[]>(config);
    return result.map((r) => AvailabilityParsers.toAssignableUnit(r));
  }

  async function getUnitHourReadings(productId: number, unitId: number) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/units/${unitId}/hourReadings`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<HourReadingParsers.HourReadingJson[]>(config);
    return result.map((r) => HourReadingParsers.toHourReadng(r));
  }
  async function updateProduct(productId: number, update: ProductUpdateRequest) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        isActive: update.isActive,
        isDeleted: update.isDeleted,
      },
    };

    const result = await handleRequest<ProductParsers.ProductJson>(config);
    return ProductParsers.toProduct(result);
  }

  async function searchLocations(request: LocationSearchPaginationRequest): Promise<Pagination<Location>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/locations/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<LocationParsers.LocationJson>>(
      config,
    );
    return {
      pagination,
      items: items.map(LocationParsers.toLocation),
    };
  }

  async function updateLocationStatus(locationId: number, enable: boolean): Promise<Location> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/locations/${locationId}/${enable ? 'enable' : 'disable'}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<LocationParsers.LocationJson>(config);
    return LocationParsers.toLocation(result);
  }

  async function upsertDeadDaysConfig(body: DeadDaysConfig): Promise<DeadDaysConfig> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/dead-days-config`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleRequest<DeadDaysConfig>(config);
  }

  async function getDeadDaysConfig(categoryId: number): Promise<DeadDaysConfig> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/dead-days-config?categoryId=${categoryId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<DeadDaysConfig>(config);
  }

  const orderApiV1 = useOrderApiV1();
  const productApiV1 = useProductApiV1();
  const reportApiV1 = useReportApiV1();
  const serviceAndRepairApi = useServiceAndRepairAPI();
  const deliveryApi = useDeliveryApi();
  const userAPI = useUserAPI();

  async function listProductAddons(productId: number, locationId: number): Promise<Product[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/${productId}/addons`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        locationId,
      },
    };

    const result = await handleRequest<ProductParsers.ProductJson[]>(config);
    return result.map(ProductParsers.toProduct);
  }

  async function upsertTagPricing(tagPricing: Omit<TagPricing, 'id'> & { id?: number }): Promise<TagPricing> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/tag-pricings`,
      headers: {
        'X-Tenant': tenant,
      },
      data: tagPricing,
    };

    return await handleRequest<TagPricing>(config);
  }

  async function deleteTagPricing(id: string): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/tag-pricings/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<void>(config);
  }

  async function getTagPricing(id: string): Promise<TagPricing> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/tag-pricings/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<TagPricing>(config);
  }

  async function getTagPricingByCustomerId(customerId: number): Promise<TagPricing> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/tag-pricings/by-customer/${customerId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<TagPricing>(config);
  }

  async function searchTagPricings(request: TagPricingSearchRequest): Promise<Pagination<TagPricing>> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/tag-pricings/search`,
      headers: {
        'X-Tenant': tenant,
      },
      params: {
        hideDeleted: request.hideDeleted,
        pageSize: request.pageSize,
        pageNumber: request.pageNumber,
        keyword: request.keyword,
      },
    };

    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<TagPricing>>(config);
    return {
      pagination,
      items: items,
    };
  }

  async function listTagPricings(hideDeleted: boolean = false): Promise<TagPricing[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/tag-pricings/list`,
      headers: {
        'X-Tenant': tenant,
      },
      params: {
        hideDeleted: hideDeleted,
      },
    };

    const items = await handleRequest<TagPricing[]>(config);
    return items;
  }

  async function getRemainingSpotsOnTag(
    customerId: number,
    tagPricingId: number,
    onRentingProductIds: number[],
  ): Promise<number> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/tag-pricings/remaining-spots/${customerId}/${tagPricingId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        onRentingProductIds,
      },
    };
    return await handleRequest<number>(config);
  }

  async function getLabel(labelKey: string): Promise<Label | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/labels/${labelKey}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<Label | undefined>(config);
  }

  async function searchAccountRequests(request: AccountSearchRequest): Promise<Pagination<AccountRequest>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/on-accounts/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const { items, pagination } = await handleRequest<
      PaginationParsers.PaginationJson<OnAccountParsers.AccountRequestJson>
    >(config);
    return {
      pagination,
      items: items.map((item) => OnAccountParsers.toAccountRequest(item)),
    };
  }

  async function approveAccount(userId: number, adminId: number): Promise<AccountRequest> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/on-accounts/${userId}/approve`,
      data: {
        adminId: adminId,
      },
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<AccountRequest>(config);
  }

  async function declineAccount(userId: number, adminId: number): Promise<AccountRequest> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/on-accounts/${userId}/decline`,
      data: {
        adminId: adminId,
      },
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<AccountRequest>(config);
  }

  async function calculateFinancialSummary(request: CalculateFinancialSummaryRequest): Promise<FinancialSummary> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/financials/calculateFinancialSummary`,
      data: {
        ...request,
      },
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<FinancialsParsers.FinancialSummaryJson>(config);
    return FinancialsParsers.toFinancialSummary(result);
  }

  async function getOauthConfig(provider: string, redirectTo?: string): Promise<{ url: string }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/oauth/clientConfig`,
      headers: {
        'X-Tenant': tenant,
      },
      params: {
        provider,
        redirectTo,
      },
    };

    return await handleRequest<{ url: string }>(config);
  }

  async function getCouponByCode(code: string) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/coupons/${code}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<CouponParsers.CouponJson>(config);
    return CouponParsers.toCoupon(result);
  }

  async function confirmAvailability(req: ConfirmAvailability): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/confirmAvailability`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return await handleRequest<void>(config);
  }

  async function createOrder(req: CreateOrderRequest | CreateQuoteRequest): Promise<Order> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    const json = await handleRequest<OrderJson>(config);
    return OrderParsers.toOrder(json);
  }

  async function searchCustomerReviews(request: OrderReviewSearchRequest): Promise<Pagination<OrderReview>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/order-reviews/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const { items, pagination } = await handleRequest<
      PaginationParsers.PaginationJson<OrderReviewParsers.OrderReviewJson>
    >(config);
    return {
      pagination,
      items: items.map((item) => OrderReviewParsers.toOrderReview(item)),
    };
  }

  async function getCustomerReviewStatistics(): Promise<OrderReviewStatistics> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/order-reviews/statistics`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<OrderReviewStatistics>(config);
  }

  async function searchProductUnits(
    req: SearchProductInventoryUnitsRequest,
  ): Promise<Pagination<ProductInventoryUnit>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/products/units/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    const { items, pagination } = await handleRequest<
      PaginationParsers.PaginationJson<ProductParsers.ProductInventoryUnitJson>
    >(config);
    return {
      pagination,
      items: items.map((item) => ProductParsers.toProductInventoryUnit(item)),
    };
  }

  async function getProductUnitsStats(
    locationId?: string | number,
    categoryId?: string | number,
    keyword?: string,
  ): Promise<{ total: number; available: number; onRent: number }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products/units/stats?${
        locationId ? `locationId=${locationId}&` : ''
      }${categoryId ? `categoryId=${categoryId}` : ''}${keyword ? `keyword=${keyword}` : ''}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest(config);
  }

  async function getWorkOrderStats(
    locationId?: string | number,
    categoryId?: string | number,
    keyword?: string,
  ): Promise<{ total: number; hardDown: number; softDown: number }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/work-order/stats?${locationId ? `locationId=${locationId}&` : ''}${
        categoryId ? `categoryId=${categoryId}$` : ''
      }${keyword ? `keyword=${keyword}` : ''}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest(config);
  }

  async function canSubmit(
    userId: number,
    paymentMethod: OrderPaymentMethod,
    cardId?: number,
    checkDocuments: boolean = true,
  ): Promise<{ isValid: boolean; errors: string[] }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/canSubmit`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        userId,
        paymentMethod,
        cardId,
        checkDocuments,
      },
    };

    return await handleRequest<{ isValid: boolean; errors: string[] }>(config);
  }

  async function approveQuote(orderId: number, paymentMethod: PaymentMethodRequest): Promise<void> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/approveQuote`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        paymentMethod,
      },
    };

    return await handleRequest<void>(config);
  }

  async function searchEmailArchives(req: SearchEmailArchivesRequest): Promise<Pagination<EmailArchive>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/emails/archives/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<EmailParsers.EmailArchiveJson>>(
      config,
    );

    return {
      pagination,
      items: items.map((item) => EmailParsers.toEmailArchive(item)),
    };
  }

  async function resendEmail(emailArchiveId: string, recipient: string): Promise<EmailArchive> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/emails/archives/${emailArchiveId}/resend`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        recipient,
      },
    };
    const result = await handleRequest<EmailParsers.EmailArchiveJson>(config);
    return result && EmailParsers.toEmailArchive(result);
  }

  async function sendQuote(orderId: number): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/emails/send-quote`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { orderId },
    };
    return await handleRequest<void>(config);
  }

  async function sendInvoice(invoiceId: string): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/invoices/${invoiceId}/send`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest(config);
  }

  async function sendEmailAsync(msg: EmailMessage): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/emails/send-async`,
      headers: {
        'X-Tenant': tenant,
      },
      data: msg,
    };
    return await handleRequest(config);
  }

  async function sendNotification(request: NotificationRequest): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/notifications/send`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    return await handleRequest<void>(config);
  }

  async function addOrderInternalNote(orderId: number, note: string) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/internalNote`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        note,
      },
    };

    const res = await handleRequest<OrderParsers.OrderInternalNotesJson>(config);
    return OrderParsers.toOrderInternalNotes(res);
  }

  async function updateOrderInternalNote(orderId: number, noteId: number, note: string) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/internalNote/${noteId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        note,
      },
    };

    const res = await handleRequest<OrderParsers.OrderInternalNotesJson>(config);
    return OrderParsers.toOrderInternalNotes(res);
  }

  async function listCancelReasons(): Promise<CancelReason[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/cancel-reasons`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<CancelReason[]>(config);
  }

  async function setOrderSecurityDeposit(orderId: number, percentage?: number, amount?: number) {
    const config = {
      method: 'patch',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/setSecurityDeposit`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        percentage,
        amount,
      },
    };

    return handleRequest<boolean>(config);
  }

  async function updateOrderPayment(orderId: number, paymentMethod: PaymentMethodRequest): Promise<void> {
    const config = {
      method: 'patch',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/paymentMethod`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        paymentMethod,
      },
    };

    return await handleRequest<void>(config);
  }

  async function calculatePriceAsAdmin(body: CalculatePriceRequest): Promise<CalculatePriceResult> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/product/prices/calculatePrice`,
      headers: {
        'X-Tenant': tenant,
      },
      data: [body],
    };
    const result = await handleRequest<CalculatePriceResult[]>(config);
    return result[0];
  }

  async function updateOrderNotes(orderId: number, note: string) {
    const config = {
      method: 'patch',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/notes`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        note,
      },
    };

    return await handleRequest<number>(config);
  }

  async function updateOrderRepresentative(orderId: number, representativeId?: string) {
    const config = {
      method: 'patch',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/representative`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        representativeId,
      },
    };

    return await handleRequest<void>(config);
  }

  async function uploadToS3(presignedUrl: string, file: File) {
    const config = {
      method: 'put',
      url: presignedUrl,
      data: file,
      headers: {
        'Content-Type': file.type,
      },
    };

    return await handleRequest<string>(config);
  }

  async function getAvailableCountByLocations(
    productId: number,
    type: OrderType = OrderType.Rental,
    from: DateTime,
    to: DateTime,
    excludedOrderIds?: number[],
  ): Promise<{ locationId: number; count: number }[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/availability/getAvailableCountByLocations`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productId,
        from,
        to,
        excludedOrderIds,
        type,
      },
    };
    return handleRequest<{ locationId: number; count: number }[]>(config);
  }

  async function getAvailableCount(
    productId: number,
    locationId: number,
    type: OrderType = OrderType.Rental,
    from: DateTime,
    to: DateTime,
    excludedOrderIds?: number[],
  ): Promise<number> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/availability/getAvailableCount`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productId,
        locationId,
        from,
        to,
        excludedOrderIds,
        type,
      },
    };
    return handleRequest<number>(config);
  }

  async function generateCategoryAiAliases(input: string): Promise<string[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/categories/generateAliases?input=${input}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<string[]>(config);
  }

  async function generateProductAiAliases(input: string): Promise<string[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/products/generateAliases?input=${input}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<string[]>(config);
  }

  async function getRentalAvailability(
    productId: number,
    locationId: number,
    quantity: number,
  ): Promise<DateTimeInterval[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/availability/getRentalAvailability`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productId,
        locationId,
        quantity,
      },
    };
    const result = await handleRequest<{ start: string | undefined; end: string | undefined }[]>(config);
    return result.map((i) => {
      return {
        start: i.start ? DateTime.fromISO(i.start, { setZone: true }) : undefined,
        end: i.end ? DateTime.fromISO(i.end, { setZone: true }) : undefined,
      };
    });
  }

  async function getDispatch(orderId: number): Promise<Dispatch | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/orders/dispatch/${orderId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<OrderParsers.DispatchJson>(config);
    return result ? OrderParsers.toDispatch(result) : undefined;
  }

  async function searchDispatch(data: SearchDispatchRequest): Promise<Dispatch[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/dispatch/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...data, date: data.date ?? DateTime.now() },
    };
    const items = await handleRequest<OrderParsers.DispatchJson[]>(config);

    return items.map(OrderParsers.toDispatch);
  }

  async function assignDriver(orderId: number, data: AssignDriverRequest): Promise<void> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/assignDriver`,
      headers: {
        'X-Tenant': tenant,
      },
      data,
    };

    return handleRequest(config);
  }

  async function updateScheduledTime(orderId: number, data: UpdateScheduledTimeRequest): Promise<void> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/updateScheduledTime`,
      headers: {
        'X-Tenant': tenant,
      },
      data,
    };

    return handleRequest(config);
  }

  async function getEvents(
    entityType: EntityType,
    entityIds: string[] | number[],
    eventType?: EventType,
  ): Promise<Event[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/events/search`,
      headers: { 'X-Tenant': tenant },
      data: { entityType, eventType, entityIds },
    };

    const result = await handleRequest<EventParsers.EventJson[]>(config);
    return result.map(EventParsers.toEvent);
  }

  async function searchMakes(keyword?: string): Promise<Make[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/taxonomy/makes${keyword ? `?keyword=${keyword}` : ''}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<Make[]>(config);
  }

  async function searchModels(makeId: string, keyword?: string): Promise<Model[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/taxonomy/makes/${makeId}/models${keyword ? `?keyword=${keyword}` : ''}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<Model[]>(config);
  }

  async function createCustomMake(make: Make): Promise<Make> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/taxonomy/makes`,
      headers: {
        'X-Tenant': tenant,
      },
      data: make,
    };

    return await handleRequest<Make>(config);
  }

  async function createCustomModel(
    makeId: string,
    model: { name: string; displayName: string; makeId: string; yearsManufactured: number[] },
  ): Promise<Model> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/taxonomy/makes/${makeId}/models`,
      headers: {
        'X-Tenant': tenant,
      },
      data: model,
    };

    return await handleRequest<Model>(config);
  }

  async function getInvoiceLogoUploadUrl(filename: string): Promise<string> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/configurations/logos`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { filename },
    };

    return await handleRequest<string>(config);
  }

  async function updateOnAccountNumberFormat(pattern: string): Promise<string> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/on-accounts/updateNumberFormat`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        pattern,
      },
    };

    return handleRequest<string>(config);
  }

  async function getValuationsByProductUnitIds(productUnitIds: number[]): Promise<ProductUnitValuation[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/boomAndBucketIntegration/valuations`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { productUnitIds },
    };
    return await handleRequest<ProductUnitValuation[]>(config);
  }

  async function listOnBoomAndBucket(data: ListOnBoomAndBucketRequest): Promise<EquipmentListingResponse> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/boomAndBucketIntegration/listings`,
      headers: {
        'X-Tenant': tenant,
      },
      data,
    };
    return await handleRequest<EquipmentListingResponse>(config);
  }

  async function getBoomAndBucketListingsByProductUnitId(productUnitId: number): Promise<EquipmentListingResponse> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/boomAndBucketIntegration/listings/${productUnitId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<EquipmentListingResponse>(config);
  }
  return {
    getAdminContext,
    getConfiguration,
    updateConfiguration,
    getMultipleConfigurations,
    ...productApiV1,
    getQuantityAvailability,
    getProductCalendar,
    getProductSchedules,
    calculateUtilization,
    getLocationById,
    listLocations,
    getOrderProductById,
    assignUnits,
    swapUnits,
    listProducts,
    getMultipleProductsByIds,
    createPrice,
    editPrice,
    deletePrice,
    listProductPrices,
    getAssignableUnits,
    ...orderApiV1,
    ...reportApiV1,
    getUnitHourReadings,
    ...serviceAndRepairApi,
    updateProduct,
    searchLocations,
    updateLocationStatus,
    upsertDeadDaysConfig,
    getDeadDaysConfig,
    ...userAPI,
    listProductAddons,
    getEmailTemplates,
    searchEmailTemplates,
    getTemplateDefault,
    getReplacementVariables,
    updateEmailTemplate,
    getTagPricing,
    getTagPricingByCustomerId,
    listTagPricings,
    searchTagPricings,
    upsertTagPricing,
    deleteTagPricing,
    getLabel,
    searchAccountRequests,
    approveAccount,
    declineAccount,
    calculateFinancialSummary,
    getOauthConfig,
    getCouponByCode,
    confirmAvailability,
    createOrder,
    searchCustomerReviews,
    getCustomerReviewStatistics,
    searchProductUnits,
    getProductUnitsStats,
    getWorkOrderStats,
    canSubmit,
    approveQuote,
    searchEmailArchives,
    resendEmail,
    sendInvoice,
    sendQuote,
    getCountry,
    sendEmailAsync,
    addOrderInternalNote,
    updateOrderInternalNote,
    listCancelReasons,
    setOrderSecurityDeposit,
    updateOrderPayment,
    calculatePriceAsAdmin,
    listProductPricesForOrder,
    updateOrderNotes,
    updateOrderRepresentative,
    uploadToS3,
    getAvailableCount,
    getAvailableCountByLocations,
    generateCategoryAiAliases,
    generateProductAiAliases,
    getRentalAvailability,
    searchDispatch,
    assignDriver,
    updateScheduledTime,
    getEvents,
    searchMakes,
    searchModels,
    createCustomMake,
    createCustomModel,
    getInvoiceLogoUploadUrl,
    sendNotification,
    ...deliveryApi,
    updateOnAccountNumberFormat,
    getValuationsByProductUnitIds,
    listOnBoomAndBucket,
    getBoomAndBucketListingsByProductUnitId,
    getDispatch,
    getRemainingSpotsOnTag,
  };
};

async function withLoginRedirect(req: AxiosRequestConfig): Promise<AxiosResponse<any, any>> {
  try {
    return await axiosInstance(req);
  } catch (e: any) {
    if (e.status === 401) {
      const params = new URLSearchParams();
      params.set('dest', window.location.pathname + window.location.search);
      const contextPath = window.location.pathname.substring(0, window.location.pathname.indexOf('/manager'));
      window.location.href = `${contextPath}/manager/admin/login?${params}`;
    }
    throw e;
  }
}

export async function handleRequest<T>(req: AxiosRequestConfig, isFile = false, signal?: AbortSignal): Promise<T> {
  if (signal) {
    req.signal = signal;
  }

  const response = await withLoginRedirect(req);
  return isFile ? response.data : response.data.data;
}

export async function handlePaginatedRequest<T>(req: AxiosRequestConfig): Promise<Pagination<T>> {
  const response = await withLoginRedirect(req);
  return { items: response.data.data, pagination: response.data.meta };
}
